/**
 * What the farm is owed (BR-24, P3-TR5).
 *
 * Sorted by the age of the oldest unpaid bill, because the report is read to
 * decide who to chase — and a phone number sits on every row, since that is
 * the action it prompts.
 */

import { Fragment, useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { formatMoney } from '../../lib/currency.js';
import { useAuth } from '../auth/AuthContext.js';
import { InvoicePaymentPanel } from './InvoicePaymentPanel.js';
import { useAging } from './api.js';

export function ReceivablesScreen(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useAging();
  const { user } = useAuth();

  /* Which customer's bills are open. The report says who owes; the bills
     underneath are what a payment is actually recorded against. */
  const [openId, setOpenId] = useState<number | null>(null);

  // Collecting money is income, which §1.4 makes a partner's job. The server
  // enforces the same on POST /invoices/:id/payments.
  const canCollect = user?.role === 'admin' || user?.role === 'partner';

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('receivables.title')}</h1>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {data && (
        <section className="mb-4 grid grid-cols-3 gap-2" aria-label={t('receivables.buckets')}>
          {[
            { key: 'current', label: '0-30', tone: 'text-gray-900' },
            { key: 'days31to60', label: '31-60', tone: 'text-amber-800' },
            { key: 'over60', label: '60+', tone: 'text-red-700' },
          ].map((bucket) => (
            <div key={bucket.key} className="rounded-xl bg-white px-3 py-3 shadow-sm">
              <p className="text-xs text-gray-500">{bucket.label}</p>
              <p className={`mt-0.5 tabular-nums text-sm font-semibold ${bucket.tone}`}>
                {formatMoney(data.totals[bucket.key as keyof typeof data.totals], { decimals: 'never' })}
              </p>
            </div>
          ))}
        </section>
      )}

      {data?.customers.length === 0 && <p className="py-8 text-center text-gray-500">{t('receivables.empty')}</p>}

      {/* Phone: one card per customer. */}
      <ul className="space-y-2 md:hidden">
        {data?.customers.map((customer) => (
          <li key={customer.customerId} className="rounded-xl bg-white p-3 shadow-sm">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate font-medium text-gray-800">{customer.customerName}</span>
              <span className="shrink-0 tabular-nums font-semibold text-gray-900">{formatMoney(customer.total)}</span>
            </div>

            <p className="mt-1 text-xs text-gray-500">
              {t('receivables.oldest', { days: customer.oldestDays })}
              {/* The action the report exists to prompt. */}
              {customer.phone !== null && ` · ${customer.phone}`}
            </p>

            <button
              type="button"
              onClick={() => setOpenId(openId === customer.customerId ? null : customer.customerId)}
              aria-expanded={openId === customer.customerId}
              className="mt-2 min-h-touch text-sm font-medium text-green-800 underline"
            >
              {t(openId === customer.customerId ? 'receivables.hideBills' : 'receivables.showBills')}
            </button>

            {openId === customer.customerId && (
              <InvoicePaymentPanel customerId={customer.customerId} canCollect={canCollect} />
            )}
          </li>
        ))}
      </ul>

      {/* Desktop: the ageing buckets side by side, which is the whole point of
          the report — a total tells you nothing about how old the debt is. */}
      {data !== undefined && data.customers.length > 0 && (
        <div className="hidden overflow-x-auto rounded-xl bg-white shadow-sm md:block">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('receivables.customer')}</th>
                <th className="num">{t('receivables.current')}</th>
                <th className="num">31–60</th>
                <th className="num">{t('receivables.over60')}</th>
                <th className="num">{t('receivables.total')}</th>
                <th className="num">{t('receivables.oldestDays')}</th>
                <th>{t('receivables.phone')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.customers.map((customer) => (
                <Fragment key={customer.customerId}>
                  <tr className="hover:bg-gray-50">
                    <td className="font-medium text-gray-900">{customer.customerName}</td>
                    <td className="num">{formatMoney(customer.current, { decimals: 'never' })}</td>
                    <td className="num">{formatMoney(customer.days31to60, { decimals: 'never' })}</td>
                    {/* The column worth acting on, so it is the one that is coloured. */}
                    <td className={`num ${customer.over60 !== '0.00' ? 'font-medium text-red-700' : ''}`}>
                      {formatMoney(customer.over60, { decimals: 'never' })}
                    </td>
                    <td className="num font-semibold">{formatMoney(customer.total, { decimals: 'never' })}</td>
                    <td className="num">{customer.oldestDays}</td>
                    <td className="text-gray-500">{customer.phone ?? '—'}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => setOpenId(openId === customer.customerId ? null : customer.customerId)}
                        aria-expanded={openId === customer.customerId}
                        className="min-h-touch whitespace-nowrap text-sm font-medium text-green-800 underline"
                      >
                        {t(openId === customer.customerId ? 'receivables.hideBills' : 'receivables.showBills')}
                      </button>
                    </td>
                  </tr>

                  {/* The bills go in a row of their own rather than inside a
                      cell, so the form gets the full width of the table. */}
                  {openId === customer.customerId && (
                    <tr>
                      <td colSpan={8} className="bg-gray-50">
                        <InvoicePaymentPanel customerId={customer.customerId} canCollect={canCollect} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
